<script setup>
import { ref, computed, watch, onMounted } from 'vue';
import MediaBankEntry from './util/media-bank-entry.vue';

const props = defineProps({
    containerRef: {
        type: String,
        required: true
    },
    uuid: {
        type: String,
        required: true
    },
    content: {
        type: Object,
        required: true
    }
});

const current = ref(null);
const goingOut = ref(null);
const goingIn = ref(null);
const pause = ref(false);

const mediaBankEntryPropertyNames = computed(() => {
    const propertyNames = [];
    for (let i = 0; i < props.content.media_bank_entries_count; i++) {
        propertyNames.push(`media_bank_entry_${i}_id`);
    }
    return propertyNames;
});

const mediaBankEntryCaptions = computed(() => {
    const captions = [];
    for (let i = 0; i < props.content.media_bank_entries_count; i++) {
        captions.push(props.content[`media_bank_entry_${i}_caption`]);
    }
    return captions;
});

const next = () => {
    pause.value = true;
    current.value = current.value + 1 >= props.content.media_bank_entries_count ? 0 : current.value + 1;
};

const prev = () => {
    pause.value = true;
    current.value = current.value - 1 < 0 ? props.content.media_bank_entries_count - 1 : current.value - 1;
};

const slideshowContainerClass = computed(() => {
    if (props.content.variant === 'row') {
        return 'relative w-auto py-4 mx-auto grid w-full'
    }
    return 'relative w-full py-4 mx-auto max-w-7xl aspect-video'
})

const slideshowContainerStyle = computed(() => {
    if (props.content.variant === 'row') {
        let itemWidth = props.content.aspect_ratio === '1:1' ? 40 : 80;
        if (props.content.item_width) {
            itemWidth = props.content.item_width
        }

        const width = (props.content.media_bank_entries_count * itemWidth) + '%';
        const colcount = props.content.media_bank_entries_count;

        return {width: width, gridTemplateColumns: 'repeat('+colcount+', minmax(0, 1fr))', gap: props.content.gap_width + 'px' }
    }
    return ''
})

const itemAspectRatioClass = computed(() => {
    if (props.content.aspect_ratio === '1:1') {
        return 'aspect-square';
    }
    return 'aspect-video';
})

const roundingClass = computed(() => {
    if (props.content?.rounding === 'siteAppearance') {
        return 'rounded-wl';
    }
    if (props.content?.rounding === 'none') {
        return 'rounded-none';
    }
    if (props.content?.rounding === 'slight') {
        return 'rounded-lg';
    }
    if (props.content?.rounding === 'medium') {
        return 'rounded-3xl';
    }
    if (props.content?.rounding === 'very') {
        return 'rounded-5xl';
    }

    return 'rounded-wl';
})

watch(current, (val) => {
    goingOut.value = current.value - 1 < 0 ? props.content.media_bank_entries_count - 1 : current.value - 1;
    goingIn.value = current.value + 1 >= props.content.media_bank_entries_count ? 0 : current.value + 1;
});

onMounted(() => {
    current.value = 0;
    if (props.content.variant === 'row') {

    } else {
        setInterval(() => {
            if (pause.value) return;
            if (current.value + 1 === parseInt(props.content.media_bank_entries_count, 10)) {
                current.value = 0;
            } else {
                current.value++;
            }
        }, 3800)
    };
});
</script>

<template>
    <div class="my-6">
        <div
            class=""
            :class="content.variant === 'row' ? 'overflow-x-scroll w-full' : 'px-4 -mx-4 md:-mx-6 lg:-mx-8 md:px-6 lg:px-8 overflow-hidden'"
        >
            <div
                :class="slideshowContainerClass"
                :style="slideshowContainerStyle"
                v-on:mouseenter="pause = true"
                v-on:mouseleave="pause = false"
            >
                <div
                    v-for="(propertyName, i) in mediaBankEntryPropertyNames"
                    :key="i"
                >
                    <media-bank-entry
                        :container-ref="containerRef"
                        :block-uuid="uuid"
                        :block-content="content"
                        :media-bank-entry-id="content[propertyName]"
                        :property-name="propertyName"
                    >
                        <template #entry="{ entry }">
                            <img
                                v-if="content.variant === 'row'"
                                :src="entry.url"
                                class="object-contain"
                                :class="[itemAspectRatioClass, roundingClass]"
                            />
                            <div
                                v-else
                                class="absolute inset-0 transition-all duration-500 transform bg-center bg-cover aspect-video shadow-custom overflow-hidden"
                                :class="[roundingClass, goingIn === i ? 'translate-x-1/2 opacity-0 scale-75 z-1' : '', current === i ? 'z-2' : '', goingOut === i ? '-translate-x-1/2 opacity-0 scale-75 z-3' : '', goingIn !== i && current !== i && goingOut !== i ? 'hidden' : '']"
                                :style="{backgroundImage: 'url(' + entry.url + ')'}"
                            ></div>
                        </template>
                        <template #spinner><span></span></template>
                    </media-bank-entry>
                </div>

                <button
                    v-if="content.variant !== 'row'"
                    v-on:click.prevent="next()"
                    class="absolute z-20 text-white transform -translate-y-1/2 top-1/2 right-4"
                >
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-12 h-12">
                        <path stroke-linecap="round" stroke-linejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5" />
                    </svg>
                </button>
                <button
                    v-if="content.variant !== 'row'"
                    v-on:click.prevent="prev()"
                    class="absolute z-20 text-white transform -translate-y-1/2 top-1/2 left-4"
                >
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-12 h-12">
                        <path stroke-linecap="round" stroke-linejoin="round" d="M15.75 19.5L8.25 12l7.5-7.5" />
                    </svg>
                </button>
            </div>
        </div>
    </div>
</template>

<style scoped>
.aspect-video {
    aspect-ratio: 16 / 9;
}
.z-1 {
    z-index: 1;
}
.z-2 {
    z-index: 2;
}
.z-3 {
    z-index: 3;
}
</style>
